import React, { useState } from "react";
import { Settings } from "lucide-react";
import ElementGuard from "./ElementGuard";
import AccessControlEditorModal from "./AccessControlEditorModal";

interface Props {
  pageKey: string;
  /** Optional override for the icon button size (default 32px square). */
  size?: number;
}

/**
 * Drop-in gear-icon button that opens the per-page access-control editor for
 * the given `pageKey`. Visible only to delegated admins of that page (and
 * Root). Identical behavior to the inline pattern used on /test-structure
 * and /quick-quiz-maker — extracted so every new page can adopt it with
 * a single line in the header.
 *
 * Usage:
 *   <PageAccessSettingsButton pageKey="online-payment-logs" />
 */
export const PageAccessSettingsButton: React.FC<Props> = ({ pageKey, size = 32 }) => {
  const [show, setShow] = useState(false);
  return (
    <>
      <ElementGuard pageKey={pageKey} adminOnly>
        <button
          onClick={() => setShow(true)}
          title="Page access control — roles & permissions"
          style={{
            width: size,
            height: size,
            padding: 0,
            borderRadius: 8,
            border: "1px solid var(--border-default)",
            background: "var(--surface-card)",
            color: "var(--brand-primary)",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Settings size={Math.max(12, size - 16)} />
        </button>
      </ElementGuard>
      <ElementGuard pageKey={pageKey} adminOnly>
        <AccessControlEditorModal
          pageKey={pageKey}
          show={show}
          onClose={() => setShow(false)}
        />
      </ElementGuard>
    </>
  );
};

export default PageAccessSettingsButton;
